import { useCallback, useState, useMemo } from 'react';
import usePerformanceTracking from 'framework-listing-lib/internal/hooks/usePerformanceTracking';
import { MEASURE_MARK_NAMES } from 'framework-listing-lib/internal/utils/performanceTracking';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { COLUMN_DEFAULT_MIN_WIDTH, COLUMN_DEFAULT_WIDTH } from 'framework-listing-lib/constants/table';
import { useHasEditColumns } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import useTableColumnConfiguration from './useTableColumnConfiguration';
import { PropertyDataSensitivity } from 'customer-data-objects/property/PropertyDataSensitivity';
const COLUMN_DEFAULT_PROPS = {
  draggable: true,
  minWidth: COLUMN_DEFAULT_MIN_WIDTH,
  resizable: true,
  sortable: true,
  width: COLUMN_DEFAULT_WIDTH
};
export default function useParseTableColumns({
  columnKeys,
  propertyMap,
  renderCustomColumn,
  total
}) {
  const performanceTracking = usePerformanceTracking(MEASURE_MARK_NAMES.TABLE_COLUMNS);
  const objectType = useObjectTypeId();
  const {
    saveColumnConfiguration,
    columnConfiguration,
    loading: loadingColumnConfig
  } = useTableColumnConfiguration();
  const hasEditColumns = useHasEditColumns();
  const hasViews = useHasViews();
  const [localColumnWidths, setLocalColumnWidths] = useState({});
  const customColumns = useMemo(() => {
    if (!renderCustomColumn) {
      return Array.from({
        length: columnKeys.length
      });
    }
    return columnKeys.map((columnId, index) => renderCustomColumn({
      columnId,
      index,
      objectType,
      total
    }));
  }, [columnKeys, objectType, renderCustomColumn, total]);

  /**
   * This method is not meant to persist column widths but rather to keep
   * a local value of column widths while the user is resizing a column.
   */
  const updateColumnWidth = useCallback((columnId, width) => {
    setLocalColumnWidths(prevTableColumns => {
      if (prevTableColumns[columnId] === width) {
        return prevTableColumns;
      }
      return Object.assign({}, prevTableColumns, {
        [columnId]: width
      });
    });
  }, []);
  const updateColumnConfiguration = useCallback(newConfig => {
    saveColumnConfiguration(newConfig).then(() => {
      setLocalColumnWidths({});
    }).catch(() => {});
  }, [saveColumnConfiguration]);

  /**
   * Create tableColumn objects based on @columnKeys and custom columns (@renderCustomColumn).
   *
   * - If a column is not defined as custom: create TableColumn object.
   * - Otherwise create TableColumn with custom props.
   */
  const tableColumns = useMemo(() => {
    if (loadingColumnConfig || customColumns.length === 0) {
      return [];
    }
    const columnsAreDraggable = hasViews && hasEditColumns;
    const cols = customColumns.map((column, index) => {
      var _columnConfiguration$;
      const columnKey = columnKeys[index];
      const columnPropertyDefinition = propertyMap[columnKey];
      const columnWidthConfig = columnConfiguration === null || columnConfiguration === void 0 || (_columnConfiguration$ = columnConfiguration[columnKey]) === null || _columnConfiguration$ === void 0 ? void 0 : _columnConfiguration$.width;
      const localColumnWidth = localColumnWidths[columnKey];
      const isHighlySensitive = columnPropertyDefinition.dataSensitivity === PropertyDataSensitivity.HIGH;

      /**
       * Default flow when apps don't define a custom column for a CRM property
       */
      if (!column) {
        const propertyTableColumn = {
          draggable: columnsAreDraggable,
          id: columnKey,
          minWidth: COLUMN_DEFAULT_PROPS.minWidth,
          order: index,
          propertyDescriptor: columnPropertyDefinition,
          resizable: COLUMN_DEFAULT_PROPS.resizable,
          sortable: !isHighlySensitive && COLUMN_DEFAULT_PROPS.sortable,
          width: localColumnWidth || columnWidthConfig || COLUMN_DEFAULT_PROPS.width
        };
        return propertyTableColumn;
      }
      const columnAsTableColumn = column;
      const tableColumn = Object.assign({}, COLUMN_DEFAULT_PROPS, columnAsTableColumn, {
        id: columnKey,
        minWidth: columnAsTableColumn.minWidth || COLUMN_DEFAULT_PROPS.minWidth,
        order: index,
        draggable: columnsAreDraggable && columnAsTableColumn.draggable !== false,
        propertyDescriptor: columnPropertyDefinition,
        width: localColumnWidth || columnWidthConfig || columnAsTableColumn.width || COLUMN_DEFAULT_PROPS.width
      });

      // use fixed column widths (and ignore column configuration) for non resizable columns
      if (!tableColumn.resizable && columnAsTableColumn.width) {
        tableColumn.width = columnAsTableColumn.width;
      }

      // Ensure that the users saved width can't be smaller than the min width
      if (tableColumn.width < tableColumn.minWidth) {
        tableColumn.width = tableColumn.minWidth;
      }

      // Never allow sorting on highly sensitive columns, the BE will reject it since they can't access
      // highly sensitive values to perform the sort
      if (isHighlySensitive) {
        tableColumn.sortable = false;
      }
      return tableColumn;
    }).filter(Boolean);
    performanceTracking.markTimeEnd();
    return cols;
  }, [columnConfiguration, columnKeys, customColumns, hasEditColumns, hasViews, loadingColumnConfig, localColumnWidths, performanceTracking, propertyMap]);
  return useMemo(() => ({
    loading: loadingColumnConfig,
    tableColumns,
    updateColumnConfiguration,
    updateColumnWidth
  }), [loadingColumnConfig, tableColumns, updateColumnConfiguration, updateColumnWidth]);
}